import cn from 'classnames';
import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';

import type { VirtualAccount } from 'models/virtualAccount';

import { useOrgNavigate } from 'hooks/useOrgNavigate';

import {
  AccountIcon,
  AccountOwnedContent,
  AccountSharedInfo,
  AccountStatusBadge
} from './parts';
import { getAccountWidgetViewState } from './utils';

type Props = {
  accountInfo: VirtualAccount;
  isFundingUpdated?: boolean;
  'data-testid'?: string;
};

export const AccountWidget = ({
  accountInfo,
  isFundingUpdated,
  'data-testid': dataTestId
}: Props) => {
  const virtualAccountNumber = accountInfo.account_number;
  const navigate = useOrgNavigate();
  const viewState = getAccountWidgetViewState(accountInfo, isFundingUpdated);
  const isInteractive = !viewState.isSharedAccount;

  const handleRedirect = useCallback(() => {
    if (isInteractive) {
      navigate(`accounts/${virtualAccountNumber}`);
    }
  }, [isInteractive, navigate, virtualAccountNumber]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!isInteractive) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleRedirect();
      }
    },
    [handleRedirect, isInteractive]
  );

  return (
    <section
      aria-label="Account widget"
      className={cn(
        'grid grid-cols-[3rem_3fr_1fr_2rem] items-center gap-2 lg:gap-4',
        'group h-14 w-full rounded-2xl px-2 lg:px-4',
        'cool-shadow border bg-white',
        {
          'border-border-muted bg-surface-muted text-content-subtle':
            viewState.isTerminatedAccount,
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-brand-subtle focus-visible:ring-offset-2':
            isInteractive,
          'cursor-pointer hover:bg-surface-selected hover:text-content-brand':
            isInteractive && !viewState.isTerminatedAccount
        }
      )}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={handleRedirect}
      onKeyDown={handleKeyDown}
      data-testid={dataTestId}
    >
      <AccountIcon
        countryCode={viewState.countryCode}
        isSharedAccount={viewState.isSharedAccount}
        isSuspendedAccount={viewState.isSuspendedAccount}
        isTerminatedAccount={viewState.isTerminatedAccount}
      />
      <div className="flex items-center justify-between truncate">
        <div>
          <div
            className={cn({
              'font-bold text-content-brand': !viewState.isTerminatedAccount,
              'text-content-subtle': viewState.isTerminatedAccount
            })}
          >
            {viewState.accountLabel}
          </div>
          <p
            className={cn('text-content-subtle', {
              'group-hover:text-content-brand-subtle':
                !viewState.isSharedAccount && !viewState.isTerminatedAccount
            })}
          >
            No. {viewState.accountNumberLabel}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AccountStatusBadge
            isSuspendedAccount={viewState.isSuspendedAccount}
            isTerminatedAccount={viewState.isTerminatedAccount}
            statusBadgeLabel={viewState.statusBadgeLabel}
          />
          {viewState.isFundingUpdated ? (
            <div className="h-fit rounded bg-status-action-required p-2 text-sm">
              Funding details updated
            </div>
          ) : null}
        </div>
      </div>
      {!viewState.isSharedAccount ? (
        <AccountOwnedContent
          balanceValue={viewState.balanceValue}
          currencySymbol={viewState.currencySymbol}
          isTerminatedAccount={viewState.isTerminatedAccount}
        />
      ) : (
        <AccountSharedInfo />
      )}
    </section>
  );
};
